import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Button, Form, Input, message, Modal, Select, Spin, Switch, Upload} from 'antd';
import ImgCrop from 'antd-img-crop';
import 'antd/es/slider/style';
import UEditor from '@/page/component/UEditor';
import {API_ROOT} from '@/env';
import {
    createHrTemplate,
    getHrTemplateCover,
    toggleHrTemplate,
    updateHrTemplate,
    uploadHrTemplateCover,
} from '@/api/hrTemplate';
import {ROUTINE_TYPES, SPECIFIC_TYPES, getVisibleSpecificTypes} from '../routineUtils';
import styles from './TemplateFormModal.module.less';

// 任务类型下拉按“当前开放的类型”生成，隐藏未开放的类型（如积分提醒）
const TYPE_OPTIONS = Object.entries({
    ...ROUTINE_TYPES,
    ...Object.fromEntries(getVisibleSpecificTypes().map(type => [type, SPECIFIC_TYPES[type]])),
}).map(([value, label]) => ({value, label}));

const COVER_MAX_SIZE = 2 * 1024 * 1024;

// 模板正文需要和原始 HTML 保持一致：
// - disabledTableInTable: 邮件模板大量使用嵌套表格，UEditor 默认会把嵌套表格拍平成纯文本；
// - allowDivTransToP: 关闭 div -> p 转换，避免结构变化；
// - 编辑器就绪后清掉 UEditor 表格插件注入的编辑辅助样式（td 边框、table 下边距）。
const TEMPLATE_EDITOR_CONFIG = {
    disabledTableInTable: false,
    allowDivTransToP: false,
};

const COMMON_PLACEHOLDERS = [
    {key: 'EmployeeChineseName', label: '中文姓名'},
    {key: 'EmployeeEnglishName', label: '英文姓名'},
    {key: 'EmployeeFullName', label: '完整姓名'},
];

// 11 个服务周年档位共用 {{ServiceYear}} 占位符
const ANNIVERSARY_TYPES = [
    'SERVICE_ANNIVERSARY_1Y',
    'SERVICE_ANNIVERSARY_2Y',
    'SERVICE_ANNIVERSARY_3Y',
    'SERVICE_ANNIVERSARY_4Y',
    'SERVICE_ANNIVERSARY_5Y',
    'SERVICE_ANNIVERSARY_6_9Y',
    'SERVICE_ANNIVERSARY_10Y',
    'SERVICE_ANNIVERSARY_15Y',
    'SERVICE_ANNIVERSARY_20Y',
    'SERVICE_ANNIVERSARY_25Y',
    'SERVICE_ANNIVERSARY_30Y',
];

const SPECIAL_PLACEHOLDERS = {
    ...Object.fromEntries(
        ANNIVERSARY_TYPES.map(type => [type, [{key: 'ServiceYear', label: '服务周年'}]])
    ),
    ANNUAL_LEAVE: [{key: 'UntilDate', label: '年假截止日'}],
    POINTS_REMINDER: [
        {key: 'PointsExpireDate', label: '积分截止日'},
        {key: 'RemainingPoints', label: '剩余积分'},
    ],
    REFERRAL_AWARD: [
        {key: 'ReferrerName', label: '推荐人姓名'},
        {key: 'Bonus', label: '奖励金额'},
    ],
};

function normalizeAttachmentUrl(url) {
    if (!url || !API_ROOT) return url;
    try {
        const parsed = new URL(url, window.location.origin);
        const marker = '/attach/files/';
        const index = parsed.pathname.indexOf(marker);
        if (index < 0) return url;
        const prefix = API_ROOT.replace(/\/$/, '');
        // 后端返回的是测试环境绝对地址，这里统一改写成当前环境的 API_ROOT 地址；
        // 重复调用不会叠加前缀（/wecomweb/attach/files/x 会被还原成同样的地址）。
        return `${prefix}${parsed.pathname.slice(index)}${parsed.search}`;
    } catch (e) {
        return url;
    }
}

function getPlaceholders(taskType) {
    if (!taskType) return [];
    return [...COMMON_PLACEHOLDERS, ...(SPECIAL_PLACEHOLDERS[taskType] || [])];
}

export default function TemplateFormModal({open, appId, editing, onClose, onSaved}) {
    const [form] = Form.useForm();
    const editorRef = React.useRef();
    const [content, setContent] = useState('');
    const [coverPreview, setCoverPreview] = useState(null);
    const [coverUploading, setCoverUploading] = useState(false);
    const [saving, setSaving] = useState(false);

    const handleEditorReady = () => {
        const editor = editorRef.current;
        if (editor && window.UE && window.UE.utils) {
            window.UE.utils.cssRule('table', '', editor.document);
        }
    };

    useEffect(() => {
        if (!open) return;
        form.resetFields();
        setContent(editing?.content || '');
        setCoverPreview(editing?.thumbUrl || null);
        form.setFieldsValue(editing || {msgType: 'mpnews', enabled: false});

        let objectUrl;
        if (!editing?.thumbUrl && editing?.thumbMediaId && editing?.id) {
            getHrTemplateCover(appId, editing.id)
                .then(blob => {
                    objectUrl = URL.createObjectURL(blob);
                    setCoverPreview(objectUrl);
                })
                .catch(() => setCoverPreview(null));
        }

        return () => {
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [open, editing, form, appId]);

    const taskType = Form.useWatch('taskType', form);
    const placeholders = getPlaceholders(taskType);

    const insertTitlePlaceholder = placeholder => {
        const current = form.getFieldValue('title') || '';
        form.setFieldsValue({title: `${current}{{${placeholder.key}}}`});
    };

    const insertBodyPlaceholder = placeholder => {
        editorRef.current?.insertHtml(`{{${placeholder.key}}}`);
    };

    const uploadCover = async ({file, onSuccess, onError}) => {
        if (file.size > COVER_MAX_SIZE) {
            message.error('封面图片不能超过 2MB');
            onError(new Error('封面图片不能超过 2MB'));
            return;
        }
        setCoverUploading(true);
        try {
            const json = await uploadHrTemplateCover(appId, file);
            form.setFieldValue('thumbMediaId', json.data.mediaId);
            form.setFieldValue('thumbUrl', json.data.url || null);
            setCoverPreview(json.data.url || URL.createObjectURL(file));
            onSuccess(json);
        } catch (error) {
            onError(error);
        } finally {
            setCoverUploading(false);
        }
    };

    const submit = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch (e) {
            return;
        }
        // 字段校验通过后再校验封面，避免封面缺失时字段级必填提示不出现（BUG-07）
        if (!form.getFieldValue('thumbMediaId')) {
            message.error('请先上传封面图');
            return;
        }
        setSaving(true);
        try {
            let templateId = editing?.id;
            if (templateId) {
                await updateHrTemplate(appId, templateId, {...values, content});
            } else {
                const json = await createHrTemplate(appId, {...values, content});
                templateId = json.data?.id;
            }
            if (values.enabled !== !!editing?.enabled)
                await toggleHrTemplate(appId, templateId, values.enabled);
            onSaved();
        } catch (e) {
            console.warn('save hr template failed', e);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open={open}
            title={editing ? '编辑模板' : '新建模板'}
            width={1000}
            confirmLoading={saving}
            onOk={submit}
            onCancel={onClose}
            style={{maxWidth: 'calc(100vw - 32px)'}}
        >
            <Form form={form} layout="vertical">
                <Form.Item name="templateName" label="模板名称" rules={[{required: true}]}>
                    <Input />
                </Form.Item>
                <Form.Item name="taskType" label="任务类型 / 通知类别" rules={[{required: true}]}>
                    <Select disabled={!!editing} options={TYPE_OPTIONS} />
                </Form.Item>
                {taskType === 'NEW_BORN' && (
                    <Form.Item name="vacationType" label="变体" rules={[{required: true}]}>
                        <Select
                            options={[
                                {value: 'MATERNITY', label: '妈妈版'},
                                {value: 'PATERNITY', label: '爸爸版'},
                            ]}
                        />
                    </Form.Item>
                )}
                <Form.Item label="占位符" extra="点击按钮后，会把占位符插入到对应输入框光标处。">
                    {placeholders.length === 0 ? (
                        <div className={styles.placeholderPanel}>该通知类别不使用动态占位符。</div>
                    ) : (
                        <>
                            <div className={styles.placeholderPanel}>
                                <div className={styles.placeholderTitle}>插入到标题</div>
                                <div className={styles.placeholderActions}>
                                    {placeholders.map(placeholder => (
                                        <Button
                                            key={placeholder.key}
                                            size="small"
                                            onClick={() => insertTitlePlaceholder(placeholder)}
                                        >
                                            {placeholder.label}
                                        </Button>
                                    ))}
                                </div>
                            </div>
                            <div className={styles.placeholderPanel}>
                                <div className={styles.placeholderTitle}>插入到正文</div>
                                <div className={styles.placeholderActions}>
                                    {placeholders.map(placeholder => (
                                        <Button
                                            key={placeholder.key}
                                            size="small"
                                            onClick={() => insertBodyPlaceholder(placeholder)}
                                        >
                                            {placeholder.label}
                                        </Button>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </Form.Item>
                <Form.Item name="title" label="图文标题" rules={[{required: true}]}>
                    <Input />
                </Form.Item>
                <Form.Item label="正文" required>
                    <div className={styles.editorShell}>
                        <UEditor
                            ref={editorRef}
                            value={content}
                            onChange={setContent}
                            onReady={handleEditorReady}
                            editorConfig={TEMPLATE_EDITOR_CONFIG}
                            category="hr_template_content"
                            itemId={editing?.id || 'hr_template'}
                            normalizeImageUrl={normalizeAttachmentUrl}
                        />
                    </div>
                </Form.Item>
                <Form.Item hidden name="thumbMediaId">
                    <Input />
                </Form.Item>
                <Form.Item hidden name="thumbUrl">
                    <Input />
                </Form.Item>
                <Form.Item
                    label="封面图"
                    required
                    extra="支持 JPG / PNG，单个文件不超过 2MB；建议尺寸比例 1068:455（上传后可裁剪）。上传后会自动保存为当前应用可用的企微素材。"
                >
                    <Spin spinning={coverUploading}>
                        <div className={styles.coverBox}>
                            {coverPreview ? (
                                <img
                                    className={styles.coverImg}
                                    src={coverPreview}
                                    alt="封面预览"
                                />
                            ) : (
                                <span>
                                    {editing?.thumbMediaId ? '已绑定封面素材' : '未上传封面'}
                                </span>
                            )}
                        </div>
                    </Spin>
                    <ImgCrop
                        modalTitle="封面图编辑"
                        modalOk="确定"
                        modalCancel="取消"
                        aspect={1068 / 455}
                        quality={0.9}
                    >
                        <Upload
                            accept="image/png,image/jpeg"
                            showUploadList={false}
                            customRequest={uploadCover}
                        >
                            <Button type="primary" ghost style={{marginTop: 8}}>
                                {editing?.thumbMediaId ? '更换封面图' : '上传封面图'}
                            </Button>
                        </Upload>
                    </ImgCrop>
                </Form.Item>
                <Form.Item name="digest" label="摘要">
                    <Input.TextArea rows={2} />
                </Form.Item>
                <Form.Item name="author" label="作者">
                    <Input />
                </Form.Item>
                <Form.Item name="enabled" label="状态" valuePropName="checked">
                    <Switch checkedChildren="启用" unCheckedChildren="停用" />
                </Form.Item>
            </Form>
        </Modal>
    );
}

TemplateFormModal.displayName = 'TemplateFormModal';

TemplateFormModal.propTypes = {
    open: PropTypes.bool.isRequired,
    appId: PropTypes.string.isRequired,
    editing: PropTypes.shape({
        id: PropTypes.string,
        content: PropTypes.string,
        thumbUrl: PropTypes.string,
        thumbMediaId: PropTypes.string,
        enabled: PropTypes.bool,
    }),
    onClose: PropTypes.func.isRequired,
    onSaved: PropTypes.func.isRequired,
};

TemplateFormModal.defaultProps = {
    editing: null,
};
